import { colorGray } from '@atlas-frontend/design-tokens';
import { createColumnHelper, ColumnHelper } from '@tanstack/react-table';
import { Flex } from 'antd';
import { useMemo } from 'react';
import {
  ClassifierMappingOverviewDto,
  cleanArray,
  PermissionCode,
  Table,
  TableOperationButton,
  TableProps,
  TextEllipsis,
  useHasPermission,
} from '@atlas-frontend/sdk';
import * as Icon from 'react-feather';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { path } from '../../paths';
import { UiLink } from '../../paths/UiLink';
import translations from './ClassifierMappingDirectoryTable.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'classifier-mapping-directory-table',
);
addTranslations(translations);

const columnHelper =
  createColumnHelper<ClassifierMappingOverviewDto>() as ColumnHelper<ClassifierMappingOverviewDto>;

interface ClassifierMappingDirectoryTableProps
  extends Omit<
    TableProps<ClassifierMappingOverviewDto>,
    'name' | 'columns' | 'scrollContainer' | 'virtual'
  > {
  onCreate: () => void;
}

export function ClassifierMappingDirectoryTable({
  onCreate,
  ...rest
}: ClassifierMappingDirectoryTableProps) {
  const { t } = useTranslation();

  const hasPermissionToManage = useHasPermission(
    PermissionCode.CLASSIFIER_MANAGE,
  );

  const cols = useMemo(
    () =>
      cleanArray([
        columnHelper.accessor('code', {
          header: t('column.code'),
          cell: (props) => (
            <TextEllipsis>
              <UiLink
                id={`classifier-mapping-${props.row.index}`}
                path={path.classifierMappingView}
                params={{ id: props.row.original.id }}
              >
                {props.renderValue()}
              </UiLink>
            </TextEllipsis>
          ),
          enableColumnFilter: true,
          meta: {
            className: 'sticky-left',
            width: '40ch',
          },
        }),
        columnHelper.accessor('name', {
          header: t('column.name'),
          enableColumnFilter: true,
          meta: {
            width: '40ch',
          },
        }),
        columnHelper.accessor(
          (rowData) =>
            `${rowData.classifierFromCode} - ${rowData.classifierFromName}`,
          {
            id: 'classifierFromCode',
            header: t('column.classifierFromCode'),
            enableColumnFilter: true,
            meta: {
              width: '30%',
              minWidth: 150,
            },
          },
        ),
        columnHelper.accessor(
          (rowData) =>
            `${rowData.classifierToCode} - ${rowData.classifierToName}`,
          {
            id: 'classifierToCode',
            header: t('column.classifierToCode'),
            enableColumnFilter: true,
            meta: {
              width: '30%',
              minWidth: 150,
            },
          },
        ),
        columnHelper.accessor('description', {
          header: t('column.description'),
          enableColumnFilter: true,
          meta: {
            width: '40%',
            minWidth: 150,
          },
        }),
        hasPermissionToManage &&
          columnHelper.display({
            id: 'operations',
            header: () => (
              <Flex justify="center" gap="small">
                <TableOperationButton
                  id="create-classifier-mapping"
                  title={t('button.add-classifier-mapping')}
                  icon={<Icon.Plus size={22} color={colorGray[7]} />}
                  onClick={() => onCreate()}
                />
              </Flex>
            ),
            enableSorting: false,
            enableColumnFilter: false,
            meta: {
              width: 50,
              className: 'sticky-right',
            },
          }),
      ]),
    [onCreate, t, hasPermissionToManage],
  );

  return (
    <Table
      name="classifier-mapping-directory-table"
      columns={cols}
      hideClearFilters
      manualFiltering
      manualSorting
      scrollContainerSelector="#classifier-mapping-directory-content"
      sticky
      virtual
      {...rest}
    />
  );
}
